/**
 * Insurance persona mapping — the one piece of the old insurance app's
 * auth layer that survived the merge (the token store and JWT decoder
 * were replaced by the patient app's `useAuth` / `@/lib/jwt`).
 *
 * A "persona" is which insurance dashboard an account lands on, derived
 * from the backend `UserRole` (mirrored in @cardioq/engine-types).
 * ADMIN / ADMIN_FULL / PLATFORM_ADMIN are super-users ('admin') with
 * access to every dashboard.
 */

import type { UserRole } from '@cardioq/engine-types';

export type Persona =
  | 'patient'
  | 'employer'
  | 'broker'
  | 'hospital'
  | 'insurer'
  | 'tpa'
  | 'admin'
  | 'superadmin';

// Map the account's backend role → which insurance dashboard it lands on.
// Mirrors backend UserRole (models/user.py).
export const ROLE_PERSONA: Record<string, Persona> = {
  PATIENT: 'patient',
  HOSPITAL_BILLING: 'hospital',
  INSURER: 'insurer',
  EMPLOYER: 'employer',
  // Read-only aggregates of the employers an operator mapped to its org.
  BROKER: 'broker',
  TPA: 'tpa',
  ADMIN: 'admin',
  ADMIN_FULL: 'admin',
  PLATFORM_ADMIN: 'admin',
  SUPER_ADMIN: 'superadmin',
};

export function personaForRole(role: string | null | undefined): Persona | null {
  return ROLE_PERSONA[(role ?? '').toUpperCase()] ?? null;
}

/**
 * Every backend role with an insurance persona — the allow-list for the
 * /insurance/* ProtectedRoute (per-persona gating happens inside the
 * section router on top of this).
 */
export const INSURANCE_ROLES = Object.keys(ROLE_PERSONA) as UserRole[];

// Display names for backend roles. The two CardioQ platform roles are branded:
//   SUPER_ADMIN  → "CardioQ Admin"  (provisions orgs/users, assigns roles — IAM)
//   ADMIN_FULL   → "Super Admin"    (god-mode access to every persona/dashboard)
// Everything else falls back to the underscored enum with spaces.
const ROLE_LABEL: Record<string, string> = {
  SUPER_ADMIN: 'CardioQ Admin',
  ADMIN_FULL: 'Super Admin',
  ADMIN: 'Super Admin', // legacy alias for ADMIN_FULL
};

export function roleLabel(role: string | null | undefined): string {
  const r = (role ?? '').toUpperCase();
  return ROLE_LABEL[r] ?? (role ?? '').replace(/_/g, ' ');
}
